import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faMagnifyingGlass,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import {
  filterSchoolFeeIncomeRecords,
  formatKes,
  formatShortDate,
  getMonthKey,
  getMonthLabel,
  normalizeAmount,
  toDateValue
} from '../../utils/financeWorkflow';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const resolveCurrency = (item = {}) => {
  const currency = item.displayCurrency || item.paymentCurrency || item.currency || 'KES';
  return currency === 'USD' ? 'USD' : 'KES';
};

const addCurrencyTotal = (totals, currency, amount) => {
  const nextTotals = { ...totals };
  nextTotals[currency] = (nextTotals[currency] || 0) + normalizeAmount(amount);
  return nextTotals;
};

const formatCurrencyValue = (amount, currency = 'KES') => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const locale = safeCurrency === 'USD' ? 'en-US' : 'en-KE';
  const digits = safeCurrency === 'USD' ? 2 : 0;

  return `${safeCurrency} ${normalizeAmount(amount).toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  })}`;
};

const formatCurrencyTotals = (totals) => {
  const parts = [];

  if (normalizeAmount(totals.KES) > 0 || (!totals.USD && totals.KES !== undefined)) {
    parts.push(formatCurrencyValue(totals.KES || 0, 'KES'));
  }

  if (normalizeAmount(totals.USD) > 0) {
    parts.push(formatCurrencyValue(totals.USD || 0, 'USD'));
  }

  return parts.length ? parts.join(' / ') : formatCurrencyValue(0, 'KES');
};

const getCourseLabel = (course = {}) => {
  return String(course.mcourseName || course.courseName || course.name || course.title || '').trim();
};

const getPaymentDate = (payment = {}) => {
  return toDateValue(payment.paymentDate || payment.timeCreated || payment.createdAt);
};

const resolveStudentName = (data = {}) => {
  return data.bio || data.displayName || data.name || data.fullName || 'Unnamed student';
};

const resolveStudentCampus = (data = {}) => {
  return data.branchName || data.campus || data.selectedCampus || 'Campus not set';
};

function FeeCollectionStanding() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [income, setIncome] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [showPendingStudents, setShowPendingStudents] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('All');
  const [campusFilter, setCampusFilter] = useState('All');

  const loadPage = useCallback(async () => {
    const [incomeSnapshot, studentSnapshot, courseSnapshot] = await Promise.all([
      getDocs(collection(db, 'Income')),
      getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
      getDocs(collection(db, 'courses'))
    ]);

    setIncome(incomeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setStudents(studentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    setCourses(courseSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
  }, [db]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        await loadPage();
      } catch (error) {
        console.error('Failed to load fee collection standing page:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [loadPage]);

  const refreshPage = useCallback(async () => {
    try {
      setRefreshing(true);
      await loadPage();
    } catch (error) {
      console.error('Failed to refresh fee collection standing:', error);
    } finally {
      setRefreshing(false);
    }
  }, [loadPage]);

  const coursesByName = useMemo(() => {
    return courses.reduce((accumulator, course) => {
      const courseLabel = getCourseLabel(course);

      if (!courseLabel) {
        return accumulator;
      }

      accumulator[normalizeText(courseLabel)] = {
        label: courseLabel,
        price: normalizeAmount(course.mcoursePrice || course.coursePrice || course.price || course.fee),
        installment: normalizeAmount(course.feeInstallments || course.monthlyFee || course.monthlyInstallment)
      };

      return accumulator;
    }, {});
  }, [courses]);

  const incomeByStudent = useMemo(() => {
    return filterSchoolFeeIncomeRecords(income).reduce((accumulator, item) => {
      const studentId = item.studentId || item.userId;

      if (!studentId) {
        return accumulator;
      }

      if (!accumulator[studentId]) {
        accumulator[studentId] = [];
      }

      accumulator[studentId].push({
        amount: normalizeAmount(item.incomeAmount || item.amount),
        currency: resolveCurrency(item),
        date: getPaymentDate(item)
      });

      return accumulator;
    }, {});
  }, [income]);

  const monthlyFeeIncomeRows = useMemo(() => {
    return filterSchoolFeeIncomeRecords(income)
      .filter((item) => getMonthKey(item.paymentDate || item.timeCreated || item.createdAt) === periodKey)
      .map((item) => ({
        ...item,
        amount: normalizeAmount(item.incomeAmount || item.amount),
        currency: resolveCurrency(item)
      }));
  }, [income, periodKey]);

  const standingRows = useMemo(() => {
    return students
      .map((student) => {
        const userType = normalizeText(student.userType);
        const userStatus = normalizeText(student.userStatus);

        if (userType !== 'student' && userStatus !== 'student') {
          return null;
        }

        const userId = student.userId || student.id;
        const courseName = String(student.course || '').trim();
        const course = coursesByName[normalizeText(courseName)];

        if (!userId || !course || course.price <= 0 || course.installment <= 0) {
          return null;
        }

        const payments = incomeByStudent[userId] || [];
        const totalPaidToDate = payments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const paidThisMonth = payments.reduce((sum, payment) => {
          return getMonthKey(payment.date) === periodKey
            ? sum + normalizeAmount(payment.amount)
            : sum;
        }, 0);
        const balanceToDate = Math.max(course.price - totalPaidToDate, 0);
        const expectedThisMonth = Math.min(course.installment, balanceToDate);

        if (expectedThisMonth <= 0) {
          return null;
        }

        const remainingThisMonth = Math.max(expectedThisMonth - paidThisMonth, 0);
        const lastPaymentDate = [...payments]
          .map((payment) => payment.date)
          .filter(Boolean)
          .sort((first, second) => second.getTime() - first.getTime())[0] || null;

        return {
          id: student.id,
          userId,
          bio: resolveStudentName(student),
          course: course.label || courseName || 'Course not set',
          campus: resolveStudentCampus(student),
          email: student.userEmail || student.email || '',
          phoneNumber: student.phoneNumber || student.phone || '',
          profilePic: student.profilePic || defaultProfilePicture,
          totalPaidToDate,
          paidThisMonth,
          expectedThisMonth,
          remainingThisMonth,
          lastPaymentDate,
          totalBalanceToDate: balanceToDate,
          paymentStatus: paidThisMonth <= 0
            ? 'Pending'
            : remainingThisMonth <= 0
              ? 'Cleared'
              : 'Partially paid'
        };
      })
      .filter(Boolean)
      .sort((first, second) => {
        if (second.remainingThisMonth !== first.remainingThisMonth) {
          return second.remainingThisMonth - first.remainingThisMonth;
        }

        if (second.expectedThisMonth !== first.expectedThisMonth) {
          return second.expectedThisMonth - first.expectedThisMonth;
        }

        return String(first.bio || '').localeCompare(String(second.bio || ''));
      });
  }, [coursesByName, incomeByStudent, periodKey, students]);

  const currentMonthStandingRows = useMemo(() => {
    return standingRows;
  }, [standingRows]);

  const pendingRows = useMemo(() => {
    return currentMonthStandingRows.filter((student) => student.paidThisMonth <= 0);
  }, [currentMonthStandingRows]);

  const partialRows = useMemo(() => {
    return currentMonthStandingRows.filter((student) => student.paidThisMonth > 0 && student.remainingThisMonth > 0);
  }, [currentMonthStandingRows]);

  const clearedRows = useMemo(() => {
    return currentMonthStandingRows.filter((student) => student.remainingThisMonth === 0);
  }, [currentMonthStandingRows]);

  const filteredPendingRows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return pendingRows.filter((student) => {
      const matchesCourse = courseFilter === 'All' || student.course === courseFilter;
      const matchesCampus = campusFilter === 'All' || String(student.campus || '').trim() === campusFilter;

      if (!matchesCourse || !matchesCampus) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        student.bio,
        student.course,
        student.campus,
        student.email,
        student.phoneNumber
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [campusFilter, courseFilter, pendingRows, searchTerm]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(currentMonthStandingRows.map((student) => student.course).filter(Boolean))).sort();
  }, [currentMonthStandingRows]);

  const uniqueCampuses = useMemo(() => {
    return Array.from(new Set(currentMonthStandingRows.map((student) => student.campus).filter(Boolean))).sort();
  }, [currentMonthStandingRows]);

  const expectedThisMonthTotal = useMemo(() => {
    return currentMonthStandingRows.reduce((sum, student) => sum + normalizeAmount(student.expectedThisMonth), 0);
  }, [currentMonthStandingRows]);

  const collectedTotals = useMemo(() => {
    return monthlyFeeIncomeRows.reduce((totals, payment) => {
      return addCurrencyTotal(totals, payment.currency, payment.amount);
    }, { KES: 0, USD: 0 });
  }, [monthlyFeeIncomeRows]);

  const collectedThisMonthKes = normalizeAmount(collectedTotals.KES);
  const remainingThisMonthTotal = useMemo(() => {
    return currentMonthStandingRows.reduce((sum, student) => sum + normalizeAmount(student.remainingThisMonth), 0);
  }, [currentMonthStandingRows]);

  const coverageRate = expectedThisMonthTotal > 0
    ? Math.min((collectedThisMonthKes / expectedThisMonthTotal) * 100, 100)
    : 0;

  const standingLabel = getMonthLabel(periodKey);

  const viewStudent = (userId) => {
    navigate('/view-student', { state: { myData: userId } });
  };

  return (
    <div className="clientsComp applicationsPage">
      <LeftBar />
      <div className="dashMainContent">
        <Dashnav />

        <div className="appsHeader">
          <div>
            <p className="appsKicker">Data Analytics</p>
            <h4>Fee Collection Standing</h4>
            <p className="appsSubtext">
              A current-month fee view showing what should be collected, what is already paid, and what remains open.
            </p>
          </div>
          <div className="appsHeaderStats">
            <div className="appsStatChip">
              <span>Expected</span>
              <strong>{formatKes(expectedThisMonthTotal)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Collected</span>
              <strong>{formatCurrencyTotals(collectedTotals)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Remaining</span>
              <strong>{formatKes(remainingThisMonthTotal)}</strong>
            </div>
            <div className="appsStatChip">
              <span>Pending</span>
              <strong>{pendingRows.length}</strong>
            </div>
          </div>
        </div>

        <div className="appsFiltersCard">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Control
              type="month"
              value={periodKey}
              onChange={(event) => setPeriodKey(event.target.value)}
              style={{ maxWidth: '190px' }}
            />
            <Button
              variant={showPendingStudents ? 'dark' : 'outline-dark'}
              onClick={() => setShowPendingStudents((value) => !value)}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              {showPendingStudents ? 'Hide pending students' : `Show pending students (${pendingRows.length})`}
            </Button>
            <Button variant="outline-dark" onClick={refreshPage} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3">
            <span className="text-muted">
              Month: <strong className="text-dark">{standingLabel}</strong>
            </span>
            <span className="text-muted">
              Cleared this month: <strong className="text-dark">{clearedRows.length}</strong>
            </span>
            <span className="text-muted">
              Partial payers: <strong className="text-dark">{partialRows.length}</strong>
            </span>
            <span className="text-muted">
              Coverage: <strong className="text-dark">{coverageRate.toFixed(0)}%</strong>
            </span>
          </div>
        </div>

        {loading ? (
          <div className="incomeStateCard">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading fee collection standing...</span>
            </Spinner>
            <p>Loading fee collection standing...</p>
          </div>
        ) : (
          <>
            {showPendingStudents && (
              <div className="appsTableCard">
                <div className="appsTableHeader">
                  <h6>
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                    Pending students
                  </h6>
                  <span>
                    {filteredPendingRows.length} showing of {pendingRows.length} pending students
                  </span>
                </div>

                <div className="appsFiltersCard mb-3">
                  <div className="appsSearch input-group">
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Search pending students..."
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </div>
                  <div className="appsFilterGroup">
                    <Form.Select className="filterSelect" value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
                      <option value="All">All Courses</option>
                      {uniqueCourses.map((course) => (
                        <option key={course} value={course}>
                          {course}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Select className="filterSelect" value={campusFilter} onChange={(event) => setCampusFilter(event.target.value)}>
                      <option value="All">All Campuses</option>
                      {uniqueCampuses.map((campus) => (
                        <option key={campus} value={campus}>
                          {campus}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>

                <div className="table-responsive">
                  <Table hover responsive className="appsTable">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Course</th>
                        <th>Campus</th>
                        <th>Last payment</th>
                        <th>Expected this month</th>
                        <th>Remaining now</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPendingRows.map((student) => (
                        <tr key={student.id}>
                          <td>
                            <div className="appsStudentCell">
                              <div className="appDefProfPic">
                                <img src={student.profilePic || defaultProfilePicture} alt="profile" />
                              </div>
                              <div>
                                <p className="appsStudentName">{student.bio}</p>
                                <span className="appsStudentMeta">
                                  {student.email || student.phoneNumber || 'Student profile'} • Paid to date {formatKes(student.totalPaidToDate)}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{student.course || '—'}</td>
                          <td>{student.campus || '—'}</td>
                          <td>{student.lastPaymentDate ? formatShortDate(student.lastPaymentDate) : 'No payment recorded this month'}</td>
                          <td>{formatKes(student.expectedThisMonth)}</td>
                          <td>{formatKes(student.remainingThisMonth)}</td>
                          <td>
                            <Badge bg="light" text="dark" className="cohortStatusBadge cohortStatusBadge--pending">
                              <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                              Pending
                            </Badge>
                          </td>
                          <td>
                            <button className="appsViewBtn" onClick={() => viewStudent(student.userId)}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                      {filteredPendingRows.length === 0 && (
                        <tr>
                          <td colSpan="8" className="appsEmptyState">
                            No pending students match the selected filters right now.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}

            {!showPendingStudents && (
              <div className="incomeStateCard">
                <h6 className="mb-2">Pending list hidden</h6>
                <p className="mb-0">
                  Use the button above to open the current month pending students list. Partial payers stay out of that list by design.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default FeeCollectionStanding;
